/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { breakpoints } from '@coursera/cds-core';

import type { ImageParams } from 'bundles/product-card/components/LearningProductCardImage';

// Falls back to the widest slot a badge is rendered in; callers that know their slot (e.g. the 80px
// mobile list card) pass imageParams so Imgix serves an image scaled to that slot instead.
const LEARNING_PATH_IMAGE_DEFAULT_WIDTH = 320;

const styles = {
  wrapper: css`
    height: 100%;
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  image: css`
    /* CDS's '.cds-CommonCard-previewImage img' rule outranks this class, so !important is the only
       way to beat its object-fit: cover -- without which the square badge gets cropped to fill the
       landscape slot. height/width need no !important: CDS already sets both to 100%, so these
       plain declarations only do anything if this renders outside a CDS card preview slot. */
    height: 100%;
    width: 100%;
    object-fit: contain !important;
    display: block;

    /* Imgix writes its maxWidth prop out as an inline max-width, which would shrink the img box
       inside the card slot -- the badge is sized by object-fit within a full-bleed box instead.
       !important is what outranks the inline declaration. */
    max-width: none !important;

    /* CDS sets no box-sizing on the image, so without this the padding below pushes the box past
       the slot and its outer edge is sheared off by the slot's overflow: hidden. */
    box-sizing: border-box;

    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-50);
    }
  `,
};

type Props = {
  src: string;
  imageParams?: ImageParams;
};

/**
 * Preview image for a learning path card. A learning path's image is a badge logo rather than a
 * promo photo, so it is contained on a tinted background instead of cropped to fill the card's
 * landscape slot.
 *
 * The image is decorative -- the card's own title carries the accessible name -- hence the empty alt.
 */
export const LearningPathProductCardImage = ({ src, imageParams }: Props) => {
  return (
    <div css={styles.wrapper}>
      <Imgix
        src={src}
        alt=""
        // Sizes the requested image, not the rendered box (styles.image neutralizes the inline
        // max-width Imgix derives from it). Width only, deliberately: clipping to the slot's height
        // too would hand back an image as narrow as the slot is tall (a square badge in a 16/9
        // slot), and the card's width is derived from the image's intrinsic width -- the cards
        // would visibly shrink.
        maxWidth={imageParams?.width ?? LEARNING_PATH_IMAGE_DEFAULT_WIDTH}
        imgParams={{
          // Always 'clip': the badge is contained rather than cropped, so it must be scaled to fit
          // the requested width instead of being trimmed to fill a box.
          fit: 'clip',
          auto: 'format, compress',
          ...(imageParams?.quality ? { quality: imageParams.quality } : {}),
        }}
        css={styles.image}
      />
    </div>
  );
};

export default LearningPathProductCardImage;
